import styled from '@emotion/styled/macro'
import {
  AutoStack,
  DayPickerStrings,
  FormikGraphQLDatePicker,
  onFormatDate,
  OrderIdPicker,
  TimeAgo,
  UserPersona,
} from 'components'
import { FormikMultiUserPicker } from 'components/PersonPicker'
import { StatusText } from 'components/StatusText'
import { Text, Text2 } from 'components/Typography'
import { FieldArray, FieldProps, Form, FormikContext } from 'formik'
import {
  FormikCheckbox,
  FormikDropdown,
  FormikMaskedTextField,
  FormikSpinButton,
  FormikTextField,
} from 'formik-office-ui-fabric-react'
import { AuthorizedField as Field } from 'forms/AuthorizedField'
import { FieldStack, FieldStackItem, Stack } from 'forms/FormStack'
import { Box } from 'grid'
import { canUpdateProving, isInProvingRole } from 'helpers'
import { useAuthContext } from 'hooks/useAuthContext'
import { useGlobalSettings } from 'hooks/useGlobalSettings'
import { useUserSuggestions } from 'hooks/useUserSuggestions'
import {
  GlobalSettingType,
  Proving,
  ProvingType,
  ProvingUpdateType,
  ProvingUserRole,
  SingleValueSetting,
  UserRole,
} from 'model'
import {
  DayOfWeek,
  Icon,
  IconButton,
  IDropdownOption,
  Label,
  PersonaSize,
  TooltipHost,
} from 'office-ui-fabric-react'
import { Position } from 'office-ui-fabric-react/lib/utilities/positioning'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useTheme } from 'theme'
import { Section } from 'views/ProvingDetailsView/Layout'
import { PageGeneralStatus } from 'views/ProvingDetailsView/PageGeneral'
import * as Yup from 'yup'

const TimePicker = styled(Field)`
  border-style: solid;
  border-color: rgb(138,  136,  134);
  border-width: 1px;
  border-radius: 2px;
  height: 32px;
  line-height: 30px;
  position: relative;
  box-sizing: border-box;
  font-size: 14px;
  font-weight: 400;
  background: rgb(255,  255,  255);
  padding: 0px 24px 0px 8px;
  overflow: hidden;
`

const summErrorMessage = 'Die Summe der Prozentangabe sollte genau 100%'

const driverTrainings: IDropdownOption[] = [
  { key: 'qBasic', text: 'Basisfahrtraining' },
  { key: 'qTurbo', text: 'Hochleistungsfahrtraining' },
  { key: 'qSupersport', text: 'Fahrtraining Supersportfahrzeuge' },
  { key: 'qOffroad', text: 'Fahrtraining Offroad' },
  { key: 'qMuLow', text: 'Fahrtraining µ-low' },
  { key: 'qNuerburgring', text: 'Fahrtraining Nürburgring' },
  { key: 'qAquaplaning', text: 'Fahrtraining Aquaplaning' },
]

const drivingTrainings: IDropdownOption[] = [
  { key: 'dbasic', text: 'Basisfahrtraining (allg. Dienst-/Erprob. -Fahrten' },
  { key: 'dexpertTurbo', text: 'Experten-Fahrtrainng Hochleistungsfahrzeuge' },
  {
    key: 'expertSupersport',
    text: 'Experten-Fahrtraining Supersportfahrzeuge',
  },
  { key: 'dOffroad', text: 'Fahrtraining Offroad' },
  { key: 'dMuLow', text: 'Fahrtraining µ-low' },
  { key: 'dNuerburgring', text: 'Fahrtraining Nürburgring' },
  { key: 'dAquaplaning', text: 'Fahrtraining Aquaplaning' },
]

const drivingLicences: IDropdownOption[] = [
  { key: 1, text: 'allgemeine Dienst- und Erprobungsfahrten' },
  { key: 2, text: 'Hochleistungsfahrzeuge inkl. Rundstrecke' },
  { key: 3, text: 'Supersportfahrzeuge inkl. Rundstrecke' },
  { key: 4, text: 'Offroad' },
  { key: 5, text: 'µ-low' },
  { key: 6, text: 'Nürburgring Nordschleife' },
  { key: 7, text: 'Fahrtraining Aquaplaning' },
]

export const ProvingDetailsGeneralFormSchemaShape = () =>
  Yup.object().shape({
    title: Yup.string().required().trim(),
    description: Yup.string().required().trim(),
    dateFrom: Yup.date().required(),
    dateTo: Yup.date()
      .required()
      .when(
        'dateFrom',
        (dateFrom: any, schema: any) => dateFrom && schema.min(dateFrom)
      ),
    department: Yup.string().required().trim(),
    orderIdDistribution: Yup.array()
      .required()
      .min(1)
      .test('sum', summErrorMessage, (orders) => {
        let sum = 0
        orders.forEach((element: { percentage: number; orderId: string }) => {
          sum += element.percentage
        })
        const isValid = sum === 100 ? true : false
        return isValid
      }),
    effectiveSumOfCosts: Yup.number(),
    responsible: Yup.array().required('Verantwortlicher ist ein Pflichtfeld'),
    deputy: Yup.array(),
    dvtWvtCheckDisabled: Yup.boolean(),
    headOfProvingCheckDisabled: Yup.boolean(),
    multiSelectEditorCheckEnabled: Yup.boolean(),
    serviceTeamIsEditing: Yup.boolean(),
    hrgEditor: Yup.array(),
    hrgIsEditing: Yup.boolean(),
    multiSelectResponsibleCheckEnabled: Yup.boolean(),
    multiSelectSubstituteCheckEnabled: Yup.boolean(),
    adminsOnlyResponsibleCheckEnabled: Yup.boolean(),
    adminsOnlySubstituteCheckEnabled: Yup.boolean(),
    enableGroups: Yup.boolean(),
    hasIncompleteBookings: Yup.boolean(),
    resourceManager: Yup.array(),
    editor: Yup.array(),
    supportNeeds: Yup.object()
      .shape({
        ewg: Yup.boolean(),
        ewi: Yup.boolean(),
        evd: Yup.boolean(),
        hv: Yup.boolean(),
        noSupport: Yup.boolean(),
      })
      .test('oneTrue', 'Exactly one support need must be true.', (value) => {
        const trueValues = Object.values(value).filter(Boolean)
        return trueValues.length > 0
      }),
  })

export type ProvingDetailsGeneralFormType = ProvingUpdateType
export class ProvingDetailsGeneralFormSchema {
  constructor() {
    return ProvingDetailsGeneralFormSchemaShape
  }
}
export const ProvingDetailsGeneralForm: React.FC<{
  proving: Proving
  formik: FormikContext<ProvingDetailsGeneralFormType>
  setPageToValid: React.Dispatch<React.SetStateAction<PageGeneralStatus>>
  pageIsValid: PageGeneralStatus
}> = ({ proving, formik, setPageToValid, pageIsValid }) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const { findUser } = useUserSuggestions()
  const [refreshKey, setRefreshKey] = useState(0)
  React.useEffect(() => {
    localStorage.removeItem(proving._id)
  }, [proving._id])

  React.useEffect(() => {
    if (formik.isValid) {
      if (formik.dirty && pageIsValid !== PageGeneralStatus.DIRTY) {
        setPageToValid(PageGeneralStatus.DIRTY)
      }
      if (!formik.dirty && pageIsValid !== PageGeneralStatus.CORRECT) {
        setPageToValid(PageGeneralStatus.CORRECT)
      }
    } else {
      if (pageIsValid !== PageGeneralStatus.INVALID) {
        setPageToValid(PageGeneralStatus.INVALID)
      }
    }
    // formik.validateForm().then(async (errors)=>{

    //   if(formik.isValid) {console.log('FOMRK IS VALID')} else{ console.log('IS INVALID FOMIRG')}
    //   if(Object.keys(errors).length) console.log('Errors is an empty object')
    //   if(errors==={}&&pageIsValid===false){
    //     setPageToValid(true)
    //     dbg(pageIsValid,'PAGE VALID FROM INSIDE THE FORM')
    //   }
    //   if(errors!=={}&&pageIsValid===true){
    //     setPageToValid(false)
    //     dbg(pageIsValid,'PAGE VALID FROM INSIDE THE FORM')
    //   }
    // })

    //  formik.validateForm().then(errors => {
    //   if (errors === {}) {
    //     setPageToValid(true)
    //   } else {
    //     setPageToValid(false)
    //   }
    // })
  }, [
    formik.values,
    formik.errors,
    formik.dirty,
    formik.isValid,
    pageIsValid,
    setPageToValid,
  ])

  const dvtWvtSetting = useGlobalSettings().getSetting(
    GlobalSettingType.DvtWvtCheck
  ) as SingleValueSetting
  const dvtWvtText = dvtWvtSetting ? dvtWvtSetting.value : ''
  const headOfProvingSetting = useGlobalSettings().getSetting(
    GlobalSettingType.HeadOfProvingCheck
  ) as SingleValueSetting
  const headOfProvingText = headOfProvingSetting
    ? headOfProvingSetting.value
    : ''
  const multiSelectEditorSetting = useGlobalSettings().getSetting(
    GlobalSettingType.MultiSelectEditorCheck
  ) as SingleValueSetting
  const multiSelectEditorText = multiSelectEditorSetting
    ? multiSelectEditorSetting.value
    : ''

  const multiSelectResponsibleSetting = useGlobalSettings().getSetting(
    GlobalSettingType.MultiSelectResponsibleCheck
  ) as SingleValueSetting
  const multiSelectResponsibleText = multiSelectResponsibleSetting
    ? multiSelectResponsibleSetting.value
    : ''

  const adminsOnlyResponsibleSetting = useGlobalSettings().getSetting(
    GlobalSettingType.AdminsOnlyResponsibleCheck
  ) as SingleValueSetting
  const adminsOnlyResponsibleText = adminsOnlyResponsibleSetting
    ? adminsOnlyResponsibleSetting.value
    : ''

  const multiSelectSubstituteSetting = useGlobalSettings().getSetting(
    GlobalSettingType.MultiSelectSubstituteCheck
  ) as SingleValueSetting
  const multiSelectSubstituteText = multiSelectSubstituteSetting
    ? multiSelectSubstituteSetting.value
    : ''
  const adminsOnlySubstituteSetting = useGlobalSettings().getSetting(
    GlobalSettingType.AdminsOnlySubstituteCheck
  ) as SingleValueSetting
  const adminsOnlySubstituteText = adminsOnlySubstituteSetting
    ? adminsOnlySubstituteSetting.value
    : ''
  const enableGroups = useGlobalSettings().getSetting(
    GlobalSettingType.EnableGroupsCheck
  ) as SingleValueSetting
  const enableGroupsText = enableGroups ? enableGroups.value : ''
  const hasIncompleteBookingsSetting = useGlobalSettings().getSetting(
    GlobalSettingType.HasIncompleteBookingsCheck
  ) as SingleValueSetting
  const hasIncompleteBookingsText = hasIncompleteBookingsSetting
    ? hasIncompleteBookingsSetting.value
    : ''
  const [editorLimit] = React.useState(() => {
    if (
      proving.type === ProvingType.Block ||
      proving.multiSelectEditorCheckEnabled
    ) {
      return undefined
    } else {
      return 1
    }
  })
  const [responsibleLimit] = React.useState(() => {
    if (
      proving.type === ProvingType.Block ||
      proving.multiSelectResponsibleCheckEnabled
    ) {
      return undefined
    } else {
      return 1
    }
  })
  const [deputyLimit] = React.useState(() => {
    if (
      proving.type === ProvingType.Block ||
      proving.multiSelectSubstituteCheckEnabled
    ) {
      return undefined
    } else {
      return 1
    }
  })
  const [resourceManagerLimit] = React.useState(1)
  const editors =
    proving.provingGround && proving.provingGround.editors
      ? proving.provingGround.editors
      : []
  const resourceManagers =
    proving.provingGround && proving.provingGround.resourceManagers
      ? proving.provingGround.resourceManagers
      : []
  const serviceTeamMembers =
    proving.provingGround && proving.provingGround.authorizedEditors
      ? proving.provingGround.authorizedEditors
      : []

  const allEditors = editors.concat(serviceTeamMembers)

  function flattenObject(ob: any) {
    const toReturn: any = {}

    for (const i in ob) {
      if (typeof ob[i] === 'object' && ob[i] !== null) {
        const flatObject: any = flattenObject(ob[i])
        for (const x in flatObject) {
          if (!flatObject.hasOwnProperty(x)) {
            continue
          }
          toReturn[i + '.' + x] = flatObject[x]
        }
      } else {
        toReturn[i] = ob[i]
      }
    }
    return toReturn
  }

  function existsInProving(orderID: string) {
    const orderUsed: {
      participants?: string
      materials?: string
      vehicles?: string
      resourceRequirements?: string
    } = {}
    const flatProving = flattenObject(proving)
    const provingProperties = Object.keys(flatProving)
    provingProperties.forEach((property) => {
      if (
        property.includes('orderId') &&
        !property.split('.')[0].includes('orderIdDistribution') &&
        flatProving[property] === orderID
      ) {
        switch (property.split('.')[0]) {
          case 'participants':
            orderUsed.participants! = 'Teilnehmer'
            break
          case 'materials':
            orderUsed.materials! = 'Material'
            break
          case 'vehicles':
            orderUsed.vehicles! = 'Fahrzeuge'
            break
          case 'resourceRequirements':
            orderUsed.resourceRequirements! = 'Ressourcenbedarf'
            break
          default:
            break
        }
      }
    })
    return Object.values(orderUsed).join(', ')
  }

  // const isProvingEditor = isInProvingRole(proving, ProvingUserRole.Editor)
  const auth = useAuthContext()
  const authUserId = auth && auth.user && auth.user._id
  const isExternal = auth.hasUserRole(UserRole.Hrg)
  const isAdmin = auth.hasUserRole(UserRole.SystemAdministrator)
  const isResourceManager =
    auth.hasUserRole(UserRole.ResourceManager) ||
    isInProvingRole(proving, ProvingUserRole.ResourceManager)

  const isOrganisation = auth.hasUserRole(UserRole.ProvingOrganisation)

  const serviceTeam = auth.hasUserRole(UserRole.ServiceTeam)
  const serviceTeamPermissions =
    proving &&
    proving.provingGround &&
    proving.provingGround.authorizedEditors &&
    proving.provingGround.authorizedEditors.find(
      (ae: any) => ae._id === authUserId
    )
  const isServiceTeam = serviceTeam && serviceTeamPermissions ? true : false
  const statusChange = {
    resolved: proving.modified,
    resolvedBy: proving.modifiedBy,
  }
  const isMaintenanceHead = isInProvingRole(
    proving,
    ProvingUserRole.MaintenanceHead
  )
  const canUpdateCurrentProving = canUpdateProving(proving)

  const currentDate = new Date()
  const minDate = isAdmin ? {} : { minDate: currentDate }
  // const isProvingResourceManager = isInProvingRole(
  //   proving,
  //   ProvingUserRole.ResourceManager
  // )
  const isProvingManager = isInProvingRole(
    proving,
    ProvingUserRole.ProvingManager
  )
  const isProvingEditor = isInProvingRole(proving, ProvingUserRole.Editor)
  const userCanUpdate = () => {
    if (proving.status === 8 || proving.status === 9) {
      return false
    } else if (isMaintenanceHead || isExternal) {
      return false
    } else if (canUpdateCurrentProving) {
      return true
    } else if (isServiceTeam) {
      return true
    } else if (
      isProvingManager &&
      (proving.status === 1 ||
        proving.status === 4 ||
        proving.status === 5 ||
        proving.status === 10)
    ) {
      return true
    } else {
      return false
    }
  }
  const canUpdateHasIncompleteBookings = () => {
    if (proving.status === 8 || proving.status === 9) {
      return false
    } else if (isMaintenanceHead) {
      return false
    } else if (isExternal) {
      return true
    } else if (canUpdateCurrentProving) {
      return true
      // } else if (isProvingEditor) {
      //   return true
    } else if (
      isProvingManager &&
      (proving.status === 1 ||
        proving.status === 4 ||
        proving.status === 5 ||
        proving.status === 10)
    ) {
      return true
    } else {
      return false
    }
  }

  const userCannotUpdateResourceManager = () => {
    if (proving.status === 8 || proving.status === 9) {
      return false
    } else if (isAdmin || isResourceManager) {
      return true
    } else {
      return false
    }
  }
  const userCannotUpdateProvingOrganisation = () => {
    if (proving.status === 8 || proving.status === 9) {
      return false
    } else if (isAdmin || isResourceManager || isServiceTeam) {
      return true
    } else {
      return false
    }
  }
  const userCanUpdateHrgEditor = () => {
    if (proving.status === 8 || proving.status === 9) {
      return false
    } else if (isAdmin || isExternal) {
      return true
    } else {
      return false
    }
  }
  const userCanSeeUpdateHrgEditor =
    isAdmin || isExternal || isOrganisation || isServiceTeam

  const [responsibleOnlyAdmins] = React.useState(() => {
    if (isExternal || isMaintenanceHead) {
      return true
    } else if (proving.adminsOnlyResponsibleCheckEnabled) {
      if (isAdmin || isResourceManager) {
        return false
      } else {
        return true
      }
    } else {
      return false
    }
  })

  const [substituteOnlyAdmins] = React.useState(() => {
    if (isExternal || isMaintenanceHead) {
      return true
    } else if (proving.adminsOnlySubstituteCheckEnabled) {
      if (isAdmin || isResourceManager || isServiceTeam) {
        return false
      } else {
        return true
      }
    } else {
      return false
    }
  })

  return (
    <Form>
      {/* // <Persist name={proving._id} /> */}
      <Section
        label={t('section.status.header')}
        stack={{
          horizontal: true,
          wrap: true,
          tokens: { childrenGap: '0 4' },
          disableShrink: true,
        }}
      >
        <AutoStack>
          <Stack.Item>
            <AutoStack>
              <StatusText status={proving!.status || 0} />
              <Box>
                <Text2 color="neutralSecondary">
                  gesetzt von{' '}
                  {statusChange &&
                    statusChange.resolvedBy &&
                    statusChange.resolvedBy.name}
                  &nbsp;
                  <TimeAgo date={statusChange.resolved!} />
                </Text2>
              </Box>
            </AutoStack>
            <AutoStack>
              {(isAdmin ||
                isResourceManager ||
                isExternal ||
                isProvingEditor ||
                isServiceTeam) && (
                <FieldStackItem
                  name="hasIncompleteBookings"
                  render={(fieldProps) => (
                    <FormikCheckbox
                      label={hasIncompleteBookingsText}
                      boxSide={'start'}
                      {...fieldProps}
                      disabled={!canUpdateHasIncompleteBookings()}
                    />
                  )}
                />
              )}
            </AutoStack>
          </Stack.Item>
        </AutoStack>
      </Section>
      {(isAdmin || isResourceManager) && (
        <Section label={t('section.effectiveSumOfCosts.header')}>
          <Field
            name="effectiveSumOfCosts"
            label={t('section.effectiveSumOfCosts.field.label')}
            component={FormikTextField}
            type="number"
            disabled={!!isExternal || !userCanUpdate()}
            placeholder="Tatsächliche Summe der Kosten in Euro"
            suffix="€"
          />
        </Section>
      )}
      <Section label={t('section.proving.header')}>
        <Stack.Item className="composite-field">
          <Label>{t('section.proving.field.type')}</Label>
          <Box py={1}>
            <Text>
              {t(`enum.provingType.${proving.type}`)}{' '}
              <TooltipHost content={`KSU-Klasse: ${proving.deletionRule}`}>
                <Icon iconName="Info" />
              </TooltipHost>
            </Text>
          </Box>
          {(isAdmin || isResourceManager) && (
            <FieldStackItem
              name="dvtWvtCheckDisabled"
              render={(fieldProps) => (
                <FormikCheckbox
                  label={dvtWvtText}
                  boxSide={'start'}
                  {...fieldProps}
                  disabled={!userCanUpdate()}
                />
              )}
            />
          )}
        </Stack.Item>
        <Stack.Item className="composite-field">
          <Label>{t('section.proving.field.provingNr')}</Label>
          <Box py={1}>
            <Text>{proving.id}</Text>
          </Box>
          {(isAdmin || isResourceManager) && (
            <FieldStackItem
              name="headOfProvingCheckDisabled"
              render={(fieldProps) => (
                <FormikCheckbox
                  label={headOfProvingText}
                  boxSide={'start'}
                  {...fieldProps}
                  disabled={!userCanUpdate()}
                />
              )}
            />
          )}
        </Stack.Item>
        <Stack.Item className="composite-field">
          <Label>{t('section.proving.field.provingGround')}</Label>
          <Box py={1}>
            <Text>{`${proving.provingGround!.title}, ${
              proving.provingGround!.countryCode
            }`}</Text>
          </Box>
          {(isAdmin || isResourceManager) && (
            <FieldStackItem
              name="enableGroups"
              render={(fieldProps) => (
                <FormikCheckbox
                  label={enableGroupsText}
                  boxSide={'start'}
                  {...fieldProps}
                  disabled={!userCanUpdate()}
                />
              )}
            />
          )}
        </Stack.Item>
        <Field
          name="title"
          label={t('section.proving.field.title')}
          required={true}
          component={FormikTextField}
          disabled={!userCanUpdate()}
        />
        <Field
          name="description"
          label={t('section.proving.field.description')}
          required={true}
          multiline={true}
          component={FormikTextField}
          disabled={!userCanUpdate()}
        />
        <Stack.Item className="composite-field">
          <Label required={true}>{t('section.proving.field.timespan')}</Label>
          <AutoStack
            horizontal={true}
            tokens={{ childrenGap: 8 }}
            verticalAlign="center"
          >
            <Field
              name="dateFrom"
              required={true}
              styles={{
                root: {
                  background: theme.palette.yellow,
                },
              }}
              render={(fieldProps: FieldProps<any>) => {
                return (
                  // @ts-ignore
                  <FormikGraphQLDatePicker
                    {...fieldProps}
                    {...minDate}
                    firstDayOfWeek={DayOfWeek.Monday}
                    strings={DayPickerStrings}
                    showWeekNumbers={true}
                    formatDate={onFormatDate as any}
                    disabled={!userCanUpdate()}
                    initialPickerDate={new Date(proving.dateFrom)}
                    styles={
                      {
                        icon: {
                          color:
                            proving &&
                            proving.pendingChanges &&
                            proving.pendingChanges.find(
                              (pc) =>
                                pc.change && pc.change.path === '$.dateFrom'
                            )
                              ? theme.palette.yellowLight
                              : 'neutralSecondary',
                        },
                      } as any
                    }
                  />
                )
              }}
            />
            &mdash;
            <Field
              name="dateTo"
              required={true}
              render={(fieldProps: FieldProps<any>) => {
                return (
                  // @ts-ignore
                  <FormikGraphQLDatePicker
                    {...fieldProps}
                    minDate={
                      fieldProps.form.values.dateFrom
                        ? new Date(fieldProps.form.values.dateFrom)
                        : undefined
                    }
                    firstDayOfWeek={DayOfWeek.Monday}
                    strings={DayPickerStrings}
                    showWeekNumbers={true}
                    formatDate={onFormatDate as any}
                    disabled={!userCanUpdate()}
                    initialPickerDate={new Date(proving.dateTo)}
                    styles={
                      {
                        icon: {
                          color:
                            proving &&
                            proving.pendingChanges &&
                            proving.pendingChanges.find(
                              (pc) => pc.change && pc.change.path === '$.dateTo'
                            )
                              ? theme.palette.yellowLight
                              : 'neutralSecondary',
                        },
                      } as any
                    }
                  />
                )
              }}
            />
          </AutoStack>
        </Stack.Item>
        <Stack.Item className="composite-field">
          <Label>{t('section.proving.field.resourceManager')}</Label>
          <Field
            itemLimit={resourceManagerLimit}
            name="resourceManager"
            render={(fieldProps: FieldProps<any>) => (
              <FormikMultiUserPicker
                customSuggestions={resourceManagers}
                {...fieldProps}
                disabled={!userCannotUpdateResourceManager()}
              />
            )}
          />
        </Stack.Item>
        <Stack.Item className="composite-field">
          <Label>{t('section.proving.field.editor')}</Label>
          {proving.hrgIsEditing &&
          proving.serviceTeamIsEditing &&
          !isAdmin &&
          !isResourceManager &&
          !isServiceTeam ? (
            <Label>
              Erprobung wird zur Zeit durch Reisebüro und ein Service-Team
              bearbeitet!
            </Label>
          ) : proving.serviceTeamIsEditing &&
            !isAdmin &&
            !isResourceManager &&
            !isServiceTeam ? (
            <Label>
              Erprobung wird zur Zeit durch ein Service-Team bearbeitet!
            </Label>
          ) : proving.hrgIsEditing &&
            !isAdmin &&
            !isResourceManager &&
            !isServiceTeam ? (
            <Label>Erprobung wird zur Zeit durch Reisebüro bearbeitet!</Label>
          ) : (
            <Field
              itemLimit={editorLimit}
              name="editor"
              render={(fieldProps: FieldProps<any>) => (
                <FormikMultiUserPicker
                  customSuggestions={allEditors}
                  {...fieldProps}
                  disabled={!userCannotUpdateProvingOrganisation()}
                />
              )}
            />
          )}

          <AutoStack>
            {(isAdmin || isResourceManager) && (
              <FieldStack tokens={{ childrenGap: 0 }}>
                <FieldStackItem
                  name="multiSelectEditorCheckEnabled"
                  render={(fieldProps) => (
                    <FormikCheckbox
                      label={multiSelectEditorText}
                      boxSide={'start'}
                      {...fieldProps}
                      disabled={!userCanUpdate()}
                    />
                  )}
                />
                <FieldStackItem
                  name="serviceTeamIsEditing"
                  render={(fieldProps) => (
                    <FormikCheckbox
                      label="Bearbeitung durch Service-Team"
                      boxSide={'start'}
                      {...fieldProps}
                      disabled={!userCanUpdate()}
                    />
                  )}
                />
                {/* <FieldStackItem
                  name="hrgIsEditing"
                  render={fieldProps => (
                    <FormikCheckbox
                      label="Bearbeitung durch Reisebüro"
                      boxSide={'start'}
                      {...fieldProps}
                      disabled={!userCanUpdate()}
                    />
                  )}
                /> */}
              </FieldStack>
            )}
          </AutoStack>
        </Stack.Item>
        {userCanSeeUpdateHrgEditor && (
          <Stack.Item className="composite-field">
            <Label>{t('section.proving.field.hrgEditor')}</Label>
            <Field
              name="hrgEditor"
              render={(fieldProps: FieldProps<any>) => (
                <FormikMultiUserPicker
                  {...fieldProps}
                  itemLimit={1}
                  disabled={!userCanUpdateHrgEditor()}
                />
              )}
            />
          </Stack.Item>
        )}
      </Section>
      {proving.provingGround!.drivingLessons && (
        <Section label={t('Fahrertrainings')}>
          <Stack.Item className="composite-field">
            <Label>{t('Termine')}</Label>
            <AutoStack
              horizontal={true}
              tokens={{ childrenGap: 8 }}
              verticalAlign="center"
            >
              <Field
                name="trainingdate"
                component={FormikGraphQLDatePicker}
                required={true}
                disabled={isExternal!}
              />
              <TimePicker
                component="input"
                type="time"
                name="trainingtime"
                required={true}
                disabled={isExternal!}
              />
            </AutoStack>
          </Stack.Item>
          <Stack.Item className="composite-field">
            <Label>{t('Fahrertraining')}</Label>
            <FieldStackItem
              name="driverTraining"
              render={(fieldProps: FieldProps<any>) => (
                <FormikDropdown
                  {...fieldProps}
                  options={driverTrainings}
                  disabled={isExternal!}
                />
              )}
            />
          </Stack.Item>
          <Stack.Item className="composite-field">
            <Label>{t('Fahrerausbildung')}</Label>
            <FieldStackItem
              name="drivingTraining"
              render={(fieldProps: FieldProps<any>) => (
                <FormikDropdown
                  {...fieldProps}
                  options={drivingTrainings}
                  disabled={isExternal!}
                />
              )}
            />
          </Stack.Item>
          <Stack.Item className="composite-field">
            <Label>{t('Fahrerlaubnis')}</Label>
            <FieldStackItem
              name="drivingLicence"
              render={(fieldProps: FieldProps<any>) => (
                <FormikDropdown
                  {...fieldProps}
                  options={drivingLicences}
                  disabled={isExternal!}
                />
              )}
            />
          </Stack.Item>
          <Stack.Item className="composite-field">
            <Label>{t('Fahrertraining Dauer')}</Label>
            <Field
              name="driverTrainingDuration"
              component={FormikMaskedTextField}
              mask="99 h"
              disabled={isExternal!}
            />
          </Stack.Item>
          <Stack.Item className="composite-field">
            <Label>{t('Fahrertraining Treffen')}</Label>
            <Field
              name="driverTrainingGathering"
              component={FormikMaskedTextField}
              mask="99 min vor Beginnuhrzeit"
              disabled={isExternal!}
            />
          </Stack.Item>
          <Stack.Item className="composite-field">
            <Label>{t('Instrukteur')}</Label>
            <Field
              itemLimit={1}
              name="instructor"
              component={FormikMultiUserPicker}
              disabled={isExternal!}
            />
          </Stack.Item>
        </Section>
      )}
      <Section label={t('section.applicant.header')}>
        <Stack.Item className="composite-field">
          <Label>{t('section.applicant.field.applicant')}</Label>
          <UserPersona
            person={proving.createdBy!}
            size={PersonaSize.extraExtraSmall}
          />
        </Stack.Item>
        <Field
          name="department"
          label={t('section.applicant.field.department')}
          required={true}
          component={FormikTextField}
          style={{ textTransform: 'uppercase' }}
          disabled={!userCanUpdate()}
        />
        <Stack.Item className="composite-field">
          <Label required={true}>
            {t('section.applicant.field.responsible')}
          </Label>
          <Field
            name="responsible"
            required={true}
            label="test"
            itemLimit={responsibleLimit}
            component={FormikMultiUserPicker}
            disabled={
              !!responsibleOnlyAdmins ||
              proving.status === 8 ||
              proving.status === 9
            }
          />
          <AutoStack tokens={{ childrenGap: 0 }}>
            {(isAdmin || isResourceManager) && (
              <FieldStackItem
                name="multiSelectResponsibleCheckEnabled"
                render={(fieldProps) => (
                  <FormikCheckbox
                    label={multiSelectResponsibleText}
                    boxSide={'start'}
                    {...fieldProps}
                    disabled={!userCanUpdate()}
                  />
                )}
              />
            )}
            {(isAdmin || isResourceManager) && (
              <FieldStackItem
                name="adminsOnlyResponsibleCheckEnabled"
                render={(fieldProps) => (
                  <FormikCheckbox
                    label={adminsOnlyResponsibleText}
                    boxSide={'start'}
                    {...fieldProps}
                    disabled={!userCanUpdate()}
                  />
                )}
              />
            )}
          </AutoStack>
        </Stack.Item>
        <Stack.Item className="composite-field">
          <Label>{t('section.applicant.field.substitute')}</Label>
          <Field
            name="deputy"
            itemLimit={deputyLimit}
            height="40"
            key={refreshKey}
            component={FormikMultiUserPicker}
            disabled={
              !!substituteOnlyAdmins ||
              proving.status === 8 ||
              proving.status === 9
            }
          />
          {isAdmin && (
            <>
              <IconButton
                iconProps={{ iconName: 'Copy' }}
                title="Copy"
                ariaLabel="Copy"
                onClick={() =>
                  navigator.clipboard.writeText(
                    formik.values.deputy?.map((x: any) => x._id).join(';') ?? ''
                  )
                }
              />
              <IconButton
                iconProps={{ iconName: 'Paste' }}
                title="Paste"
                ariaLabel="Paste"
                onClick={async () => {
                  const text = await navigator.clipboard.readText()

                  const ids = text.split(';')
                  const usersPromises = ids.map((id) => findUser(id))

                  const users = await Promise.all(usersPromises)
                  const validUsers = users.filter(Boolean)
                  formik.setFieldValue('deputy', [
                    ...(formik.values?.deputy ?? []),
                    ...validUsers,
                  ])
                  // Force a re-render by updating the state
                  setRefreshKey((oldKey) => oldKey + 1)
                }}
              />
            </>
          )}
          <AutoStack tokens={{ childrenGap: 0 }}>
            {(isAdmin || isResourceManager) && (
              <FieldStackItem
                name="multiSelectSubstituteCheckEnabled"
                render={(fieldProps) => (
                  <FormikCheckbox
                    label={multiSelectSubstituteText}
                    boxSide={'start'}
                    {...fieldProps}
                    disabled={!userCanUpdate()}
                  />
                )}
              />
            )}
            {(isAdmin || isResourceManager) && (
              <FieldStackItem
                name="adminsOnlySubstituteCheckEnabled"
                render={(fieldProps) => (
                  <FormikCheckbox
                    label={adminsOnlySubstituteText}
                    boxSide={'start'}
                    {...fieldProps}
                    disabled={!userCanUpdate()}
                  />
                )}
              />
            )}
          </AutoStack>
        </Stack.Item>
      </Section>

      <Section label={t('section.supportNeeds.header')}>
        <Label required={true}>{t('section.supportNeeds.info')}</Label>
        <Field
          name="supportNeeds.ewg"
          render={(fieldProps: FieldProps) => (
            <FormikCheckbox
              label={t('section.supportNeeds.field.ewg')}
              boxSide={'start'}
              {...fieldProps}
              disabled={
                !(isAdmin || (isProvingManager && proving.status === 1)) ||
                fieldProps.form.values.supportNeeds.noSupport
              }
            />
          )}
        />
        <Field
          name="supportNeeds.ewi"
          render={(fieldProps: FieldProps) => (
            <FormikCheckbox
              label={t('section.supportNeeds.field.ewi')}
              boxSide={'start'}
              {...fieldProps}
              disabled={
                !(isAdmin || (isProvingManager && proving.status === 1)) ||
                fieldProps.form.values.supportNeeds.noSupport
              }
            />
          )}
        />
        <Field
          name="supportNeeds.evd"
          render={(fieldProps: FieldProps) => (
            <FormikCheckbox
              label={t('section.supportNeeds.field.evd')}
              boxSide={'start'}
              {...fieldProps}
              disabled={
                !(isAdmin || (isProvingManager && proving.status === 1)) ||
                fieldProps.form.values.supportNeeds.noSupport
              }
            />
          )}
        />
        <Field
          name="supportNeeds.hv"
          render={(fieldProps: FieldProps) => (
            <FormikCheckbox
              label={t('section.supportNeeds.field.hv')}
              boxSide={'start'}
              {...fieldProps}
              disabled={
                !(isAdmin || (isProvingManager && proving.status === 1)) ||
                fieldProps.form.values.supportNeeds.noSupport
              }
            />
          )}
        />

        {/* Cutto */}
        <Field
          name="supportNeeds.noSupport"
          render={(fieldProps: FieldProps) => (
            <FormikCheckbox
              label={t('section.supportNeeds.field.noSupport')}
              boxSide={'start'}
              defaultChecked={true}
              {...fieldProps}
              disabled={
                !(isAdmin || (isProvingManager && proving.status === 1)) ||
                fieldProps.form.values.supportNeeds.ewg ||
                fieldProps.form.values.supportNeeds.ewi ||
                fieldProps.form.values.supportNeeds.evd ||
                fieldProps.form.values.supportNeeds.hv
              }
            />
          )}
        />
      </Section>

      <Section label={t('section.orderIds.header')}>
        <FieldArray name="orderIdDistribution">
          {({ form, name, ...helpers }) => {
            const values: { orderId: string; percentage: number }[] =
              form.values[name]

            return (
              <AutoStack tokens={{ childrenGap: 4 }}>
                {values.map((x: any, i: any) => (
                  <AutoStack
                    horizontal={true}
                    verticalAlign="center"
                    tokens={{ childrenGap: 8 }}
                    key={i}
                  >
                    <Field
                      name={`orderIdDistribution[${i}].orderId`}
                      render={(props: any) => (
                        <FormikTextField
                          readOnly={true}
                          styles={{ field: { width: 178 } }}
                          {...props}
                          disabled={!userCanUpdate()}
                        />
                        // <FormikOrderIdDropdown
                        //   styles={{ title: { width: 175 } }}
                        //   placeholders={{
                        //     default: t('section.orderIds.field.orderId'),
                        //   }}
                        //   {...props}
                        //   disabled={true}
                        // />
                      )}
                    />
                    <Field
                      name={`orderIdDistribution[${i}].percentage`}
                      render={(props: any) => {
                        return (
                          <FormikSpinButton
                            styles={{
                              input: { width: 40 },
                              label: { marginLeft: -7 },
                            }}
                            onValidate={(value: string) => {
                              if (
                                Number(value) > 100 ||
                                Number(value) < 0 ||
                                value.trim().length === 0 ||
                                isNaN(+value)
                              ) {
                                if (
                                  props.form.initialValues
                                    .orderIdDistribution &&
                                  props.form.initialValues.orderIdDistribution[
                                    i
                                  ]
                                ) {
                                  return Number(
                                    props.form.initialValues
                                      .orderIdDistribution[i].percentage
                                  )
                                } else {
                                  return 0
                                }
                              } else {
                                return Number(value)
                              }
                            }}
                            min={0}
                            label="%"
                            labelPosition={Position.end}
                            max={100}
                            step={5}
                            {...props}
                            disabled={!userCanUpdate()}
                          />
                        )
                      }}
                    />
                    <Stack.Item grow={1}>
                      <Field
                        name={`orderIdDistribution[${i}].annotation`}
                        render={(props: any) => (
                          <FormikTextField
                            placeholder={t('section.orderIds.field.term')}
                            disabled={!userCanUpdate()}
                            styles={{ fieldGroup: { flex: '1' } }}
                            {...props}
                          />
                        )}
                      />
                    </Stack.Item>
                    <IconButton
                      styles={{ root: { color: 'red' } }}
                      iconProps={{ iconName: 'Delete' }}
                      disabled={!userCanUpdate()}
                      onClick={() => {
                        const orderIdUsed = existsInProving(x.orderId as string)
                        if (orderIdUsed) {
                          form.setFieldError(
                            'orderIdDistribution',
                            `Diese Auftragsnummer kann nicht verändert oder gelöscht werden, da sie in ${orderIdUsed} verwendet wird.`
                          )
                          return
                        }
                        helpers.remove(i)
                      }}
                    />
                  </AutoStack>
                ))}
                {/* <OrderIdPicker
                  blacklist={values.map(v => v.orderId)}
                  styles={{ input: { width: 175 } }}
                  inputProps={{
                    placeholder: '+ ' + t('section.orderIds.field.orderId'),
                  }}
                  onChanged={(option: { key: any }) => {
                    if (option.key) {
                      helpers.push({
                        orderId: option.key,
                        percentage:
                          100 -
                          values.reduce((sum, x) => sum + x.percentage, 0),
                      })
                    }
                  }}
                /> */}
                <OrderIdPicker
                  blacklist={values.map((v) => v.orderId)}
                  styles={{ text: { width: 175 } }}
                  inputProps={{
                    placeholder: '+ ' + t('section.orderIds.field.orderId'),
                  }}
                  itemLimit={1}
                  disabled={!userCanUpdate()}
                  onItemSelected={(item) => {
                    if (item) {
                      helpers.push({
                        orderId: item.key,
                        percentage:
                          100 -
                          values.reduce((sum, x) => sum + x.percentage, 0),
                        annotation: '',
                      })
                      return null
                    }
                    return null
                  }}
                />

                <AutoStack
                  horizontal={true}
                  verticalAlign="center"
                  tokens={{ childrenGap: 8 }}
                >
                  <Box width="175px" style={{ textAlign: 'right' }} px={1}>
                    {t('section.orderIds.field.sum')}
                  </Box>
                  <Box px={1}>
                    {values.reduce((sum, x) => sum + x.percentage, 0)}%
                  </Box>
                </AutoStack>
                <div style={{ color: 'red' }}>
                  {form.errors.orderIdDistribution}
                </div>
              </AutoStack>
            )
          }}
        </FieldArray>
      </Section>
    </Form>
  )
}
